import React, { useEffect, useMemo, useState } from "react";
import {
  Link as RouterLink,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  FormControlLabel,
  Link,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import YouTubeIcon from "@mui/icons-material/YouTube";
import apiService from "../services/api";
import { ROUTES_NAMES } from "../components/Layout";
import { matchesProgramAnalysisRouteTaxonomy } from "../constants/programAnalysisTaxonomy";
import { getProgramImageUrl } from "../utils/imageUtils";
import { DailyMetricsGrowthPctChart } from "./ProgramAnalysis/DailyMetricsGrowthPctChart";
import { DailyMetricsLineChart } from "./ProgramAnalysis/DailyMetricsLineChart";
import {
  buildDailyMetricsChartPoints,
  buildGrowthPercentOfTotalChartPoints,
  DAILY_METRICS_VIEWS_COLOR,
  getDefaultProgramMetricsDateRange,
  type DailyMetricsXAxisMode,
} from "./ProgramAnalysis/dailyMetricsChart";

const ProgramDailyMetricsPage: React.FC = () => {
  const { programId } = useParams<{ programId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaults = useMemo(() => getDefaultProgramMetricsDateRange(), []);

  const [startDate, setStartDate] = useState(
    () => searchParams.get("startDate") || defaults.startDate,
  );
  const [endDate, setEndDate] = useState(
    () => searchParams.get("endDate") || defaults.endDate,
  );
  const [xAxisMode, setXAxisMode] = useState<DailyMetricsXAxisMode>("days");
  const [showViews, setShowViews] = useState(true);
  const [showLikes, setShowLikes] = useState(false);
  const [showComments, setShowComments] = useState(false);

  useEffect(() => {
    const next = new URLSearchParams();
    if (startDate) next.set("startDate", startDate);
    if (endDate) next.set("endDate", endDate);
    setSearchParams(next, { replace: true });
  }, [startDate, endDate, setSearchParams]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["program-episodes-daily-metrics", programId, startDate, endDate],
    queryFn: () =>
      apiService.getProgramEpisodesDailyMetrics(programId!, {
        startDate,
        endDate,
      }),
    enabled: Boolean(programId),
  });

  const program = data?.program;
  const episodes = useMemo(() => data?.episodes ?? [], [data?.episodes]);
  const programOutOfScope =
    program?.podcast != null && !matchesProgramAnalysisRouteTaxonomy(program.podcast);

  const growthPctChartData = useMemo(
    () =>
      buildGrowthPercentOfTotalChartPoints(
        episodes.map((ep) => ({
          dailyMetrics: ep.dailyMetrics.map((m) => ({
            ...m,
            publishedAt: ep.publishedAt,
          })),
        })),
        xAxisMode,
      ),
    [episodes, xAxisMode],
  );

  const peakGrowthDay = useMemo(
    () => growthPctChartData.find((p) => p.isPeakIncremental),
    [growthPctChartData],
  );

  const episodesWithMetrics = useMemo(
    () => episodes.filter((ep) => ep.dailyMetrics.length > 0),
    [episodes],
  );

  const programImageUrl = useMemo(() => {
    if (!program?.imageS3Key) return undefined;
    return getProgramImageUrl({ imageS3Key: program.imageS3Key });
  }, [program]);

  const chartProps = { showViews, showLikes, showComments };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Button
        component={RouterLink}
        to={ROUTES_NAMES.PROGRAM_ANALYSIS}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
      >
        Volver a programas
      </Button>

      {isLoading ? (
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Skeleton variant="rounded" height={72} />
          <Skeleton variant="rounded" height={56} />
        </Stack>
      ) : isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error)?.message || "No se pudo cargar el programa."}
        </Alert>
      ) : programOutOfScope ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Este programa no pertenece a un canal Other · Digital. Solo esos canales están disponibles en Programas.
        </Alert>
      ) : (
        <>
      {program ? (
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Avatar src={programImageUrl} sx={{ width: 56, height: 56 }}>
            {program.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box minWidth={0}>
            <Typography variant="h5" fontWeight="bold" noWrap>
              {program.name}
            </Typography>
            {program.podcast && (
              <Chip
                label={program.podcast.title}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            )}
          </Box>
        </Stack>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Episodios publicados entre
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          flexWrap="wrap"
        >
          <TextField
            label="Desde"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Hasta"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={xAxisMode}
            onChange={(_, v) => v && setXAxisMode(v)}
          >
            <ToggleButton value="days">Días desde publicación</ToggleButton>
            <ToggleButton value="calendar">Fecha</ToggleButton>
          </ToggleButtonGroup>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <FormControlLabel
              control={
                <Switch
                  checked={showViews}
                  onChange={(e) => setShowViews(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: DAILY_METRICS_VIEWS_COLOR,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: DAILY_METRICS_VIEWS_COLOR,
                    },
                  }}
                />
              }
              label="Views"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showLikes}
                  onChange={(e) => setShowLikes(e.target.checked)}
                  color="warning"
                />
              }
              label="Likes"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showComments}
                  onChange={(e) => setShowComments(e.target.checked)}
                  color="secondary"
                />
              }
              label="Comments"
            />
          </Stack>
        </Stack>
        {!isLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {episodes.length} episodio{episodes.length === 1 ? "" : "s"} en el
            rango
            {episodesWithMetrics.length > 0 &&
              ` · ${episodesWithMetrics.length} con daily metrics`}
          </Typography>
        )}
      </Paper>

      {isLoading ? (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={360} />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={360} />
          ))}
        </Stack>
      ) : episodes.length === 0 ? (
        <Alert severity="info">
          No hay episodios de larga duración en el rango seleccionado.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {growthPctChartData.length > 0 && (
            <Card variant="outlined" sx={{ borderColor: DAILY_METRICS_VIEWS_COLOR, borderWidth: 1 }}>
              <CardContent>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={1}
                  sx={{ mb: 1 }}
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    <TrendingUpIcon fontSize="small" sx={{ color: DAILY_METRICS_VIEWS_COLOR }} />
                    <Typography variant="h6" fontWeight="bold">
                      % de crecimiento vs total de views
                    </Typography>
                  </Stack>
                  {peakGrowthDay && (
                    <Chip
                      size="small"
                      label={`Mayor upside: ${peakGrowthDay.label} (+${peakGrowthDay.incrementalPct.toFixed(1)}% del total)`}
                      sx={{
                        bgcolor: `${DAILY_METRICS_VIEWS_COLOR}22`,
                        color: DAILY_METRICS_VIEWS_COLOR,
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Promedio del programa: qué día aporta más views respecto al total
                  final de cada episodio. La barra más oscura es el mayor upside.
                </Typography>
                <DailyMetricsGrowthPctChart data={growthPctChartData} />
              </CardContent>
            </Card>
          )}

          <Typography variant="subtitle1" fontWeight="bold" sx={{ pt: 1 }}>
            Por episodio
          </Typography>

          {episodes.map((episode) => {
            const metricsWithPublished = episode.dailyMetrics.map((m) => ({
              ...m,
              publishedAt: episode.publishedAt,
            }));
            const chartData = buildDailyMetricsChartPoints(
              metricsWithPublished,
              xAxisMode,
            );
            const hasMetrics = chartData.length > 0;
            const publishedLabel = episode.publishedAt
              ? new Date(episode.publishedAt).toLocaleDateString("es-CL", {
                  timeZone: "America/Santiago",
                })
              : "—";

            return (
              <Card key={episode._id} variant="outlined">
                <CardContent>
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    gap={1}
                    sx={{ mb: 1 }}
                  >
                    <Box minWidth={0}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {episode.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Publicado: {publishedLabel}
                        {episode.duration
                          ? ` · ${Math.round(episode.duration / 60)} min`
                          : ""}
                      </Typography>
                    </Box>
                    {episode.url && (
                      <Link
                        href={episode.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Abrir en YouTube"
                      >
                        <YouTubeIcon color="error" />
                      </Link>
                    )}
                  </Stack>

                  {hasMetrics ? (
                    <DailyMetricsLineChart
                      data={chartData}
                      {...chartProps}
                    />
                  ) : (
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={1}
                      sx={{ py: 2 }}
                    >
                      <TrendingUpIcon fontSize="small" color="disabled" />
                      <Typography variant="body2" color="text.secondary">
                        No hay daily metrics para este episodio.
                      </Typography>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
        </>
      )}
    </Container>
  );
};

export default ProgramDailyMetricsPage;
